import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'en', name: t('lang.english') },
    { code: 'yo', name: t('lang.yoruba') },
    { code: 'ha', name: t('lang.hausa') },
    { code: 'ig', name: t('lang.igbo') },
    { code: 'pcm', name: t('lang.pidgin') },
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLanguageDropdownOpen(false);
    // Force re-render of all components by updating localStorage
    localStorage.setItem('i18nextLng', langCode);
  };

  const navLinks = [
    { name: t('nav.home'), path: '/', icon: 'ri-home-5-line' },
    { name: t('nav.directory'), path: '/directory', icon: 'ri-building-line' },
    { name: t('nav.events'), path: '/events', icon: 'ri-calendar-event-line' },
    { name: t('nav.ministries'), path: '/ministries', icon: 'ri-team-line' },
    { name: t('nav.live'), path: '/live', icon: 'ri-live-line' },
    { name: t('nav.tools'), path: '/tools', icon: 'ri-tools-line' },
    { name: 'Resources Hub', path: '/resources-hub', icon: 'ri-folder-open-line' },
  ];

  return (
    <nav className={`fixed w-full top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            <img 
              src="https://static.readdy.ai/image/b26ccc3ae7fa45b3398224fbc482abd6/074d3102ffad2a455591f49f105c88ed.png" 
              alt="SDAChurch Worship Hub Nigeria" 
              className="h-9 w-auto"
            />
            <div className="flex flex-col">
              <span className={`text-sm font-semibold leading-tight ${isScrolled ? 'text-gray-900' : 'text-white'}`} style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
                Worship Hub
              </span>
              <span className={`text-xs leading-tight ${isScrolled ? 'text-gray-600' : 'text-white/90'}`}>
                SDA Churches in Nigeria
              </span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center space-x-2 flex-1 justify-center mx-8">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap cursor-pointer relative ${
                    isActive
                      ? isScrolled
                        ? 'text-emerald-600 bg-emerald-50'
                        : 'text-white bg-white/20'
                      : isScrolled 
                        ? 'text-gray-700 hover:text-emerald-600 hover:bg-emerald-50' 
                        : 'text-white hover:bg-white/10'
                  }`}
                >
                  {link.name}
                  {isActive && (
                    <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full ${
                      isScrolled ? 'bg-emerald-600' : 'bg-white'
                    }`}></span>
                  )}
                </Link>
              );
            })}
          </div>

          <div className="hidden lg:flex items-center space-x-2 flex-shrink-0 mr-16">
            {/* Login Button */}
            <a
              href="/admin-login"
              className={`flex items-center justify-center w-10 h-10 rounded-lg transition-all cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-emerald-600 hover:bg-emerald-50' : 'text-white hover:bg-white/10'
              }`}
              title="Admin Login"
            >
              <i className="ri-user-line text-xl"></i>
            </a>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors cursor-pointer ${
                  isScrolled ? 'text-gray-700 hover:bg-gray-50' : 'text-white hover:bg-white/10'
                }`}
                title={currentLanguage.name}
              >
                <i className="ri-global-line text-xl"></i>
              </button>
              
              {isLanguageDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-gray-50 cursor-pointer transition-colors ${
                        currentLanguage.code === lang.code ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700'
                      }`}
                    >
                      <span className="whitespace-nowrap">{lang.name}</span>
                      {currentLanguage.code === lang.code && (
                        <i className="ri-check-line text-base ml-auto"></i>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-md cursor-pointer transition-colors ${
              isScrolled ? 'text-emerald-600' : 'text-white'
            }`}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`block px-4 py-3 rounded-md text-sm font-medium cursor-pointer transition-colors ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              );
            })}
            <div className="pt-4 border-t border-gray-200 space-y-2">
              <Link
                to="/admin-login"
                className="flex items-center space-x-3 px-4 py-3 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-user-line text-lg"></i>
                <span>{t('nav.adminLogin')}</span>
              </Link>
              <div className="px-4 py-2">
                <p className="text-xs font-medium text-gray-500 mb-2">{t('nav.language')}</p>
                <div className="space-y-1">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        handleLanguageChange(lang.code);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors ${
                        currentLanguage.code === lang.code 
                          ? 'bg-emerald-50 text-emerald-700' 
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="whitespace-nowrap">{lang.name}</span>
                      {currentLanguage.code === lang.code && (
                        <i className="ri-check-line text-base ml-auto"></i>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
